/**
 * Frontend service for notifications (B5).
 *
 * Wraps the REST endpoints in `/api/v1/notifications` and exposes a small
 * TanStack Query surface for the bell + the inbox page. Real-time updates
 * come from the existing socket connection (mention:new event), wired via
 * `subscribeToMentions` and the `useUnreadCount` hook below.
 */
import { useCallback, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import type {
  NotificationItem,
  UnreadCountResponse,
  MentionNewEvent,
  NotificationNewEvent,
} from '@atlas/shared';
import { SOCKET_EVENTS, notificationPath } from '@atlas/shared';
import { useSocket } from '../contexts/SocketContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

let csrfTokenRef: { value: string | null } | null = null;

/** Allow `services/api.ts` to register the in-memory CSRF token getter. */
export function bindNotificationsCsrf(getter: () => string | null) {
  csrfTokenRef = { get value() { return getter(); } } as { value: string | null };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (
    method !== 'GET' &&
    method !== 'HEAD' &&
    csrfTokenRef &&
    csrfTokenRef.value
  ) {
    headers['x-csrf-token'] = csrfTokenRef.value;
  }
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error(body?.error?.message || `Request failed (${res.status})`);
  }
  return body?.data as T;
}

export interface ListNotificationsParams {
  unreadOnly?: boolean;
  limit?: number;
  before?: string;
}

function buildListQuery(params: ListNotificationsParams = {}): string {
  const search = new URLSearchParams();
  if (params.unreadOnly) search.set('unreadOnly', 'true');
  if (params.limit) search.set('limit', String(params.limit));
  if (params.before) search.set('before', params.before);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const notifications = {
  list: (params: ListNotificationsParams = {}) =>
    request<NotificationItem[]>(`/notifications${buildListQuery(params)}`),

  unreadCount: () =>
    request<UnreadCountResponse>('/notifications/unread-count'),

  markRead: (id: string) =>
    request<NotificationItem>(`/notifications/${id}/read`, {
      method: 'POST',
    }),

  markAllRead: () =>
    request<{ markedCount: number }>('/notifications/read-all', {
      method: 'POST',
    }),
};

// ----- TanStack Query hooks -----

/**
 * Query keys are namespaced under `['notifications', ...]` so the bell and
 * page can invalidate each other cleanly.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  list: (params: ListNotificationsParams = {}) =>
    [...notificationKeys.all, 'list', params] as const,
};

/**
 * Subscribe to `mention:new` on the active socket. Calls `onMention` with
 * the wire event whenever a new mention arrives for the current user. The
 * subscription is automatically torn down when the socket changes or the
 * component unmounts.
 */
export function useMentionSubscription(onMention: (event: MentionNewEvent) => void): void {
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return undefined;
    const handler = (event: MentionNewEvent) => onMention(event);
    socket.on(SOCKET_EVENTS.MENTION_NEW, handler);
    return () => {
      socket.off(SOCKET_EVENTS.MENTION_NEW, handler);
    };
  }, [socket, onMention]);
}

/**
 * Subscribe to `notification:new` — a product-watch event (#1038). Kept apart
 * from `mention:new` so a watch event is never presented as a mention.
 */
export function useWatchNotificationSubscription(
  onNotification: (event: NotificationNewEvent) => void,
): void {
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return undefined;
    socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, onNotification);
    return () => {
      socket.off(SOCKET_EVENTS.NOTIFICATION_NEW, onNotification);
    };
  }, [socket, onNotification]);
}

/**
 * Hook for the bell badge. Returns the unread count and auto-invalidates on
 * `mention:new`. Falls back to 60-second polling when the socket is
 * disconnected (covers transient outages, mobile background, etc.).
 */
export function useUnreadCount() {
  const queryClient = useQueryClient();
  const { connectionState } = useSocket();

  const query = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notifications.unreadCount,
    // Poll every 60s when disconnected; rely on socket otherwise.
    refetchInterval: connectionState === 'connected' ? false : 60_000,
    // Keep the badge accurate when returning to the tab.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);
  useMentionSubscription(invalidate);
  useWatchNotificationSubscription(invalidate);

  return query;
}

/** Single-page recent list (used by the bell popover). */
export function useNotificationList(params: ListNotificationsParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notifications.list(params),
    staleTime: 15_000,
  });
}

/** Infinite list for the full /notifications page. */
export function useNotificationInfiniteList(params: ListNotificationsParams = {}) {
  const pageSize = params.limit ?? 25;

  return useInfiniteQuery<
    NotificationItem[],
    Error,
    InfiniteData<NotificationItem[]>,
    ReturnType<typeof notificationKeys.list>,
    string | undefined
  >({
    queryKey: notificationKeys.list({ ...params, limit: pageSize }),
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      notifications.list({
        ...params,
        limit: pageSize,
        before: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < pageSize) return undefined;
      return lastPage[lastPage.length - 1].createdAt;
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notifications.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Map a notification's referenced entity to an in-app route. Returns null
 * when there's no obvious destination (so the caller can render a static
 * row rather than a link).
 *
 * The mapping itself lives in `@atlas/shared` so that outbound email deep
 * links and in-app navigation resolve identically.
 */
export function notificationHref(n: NotificationItem): string | null {
  const productId = typeof n.metadata?.productId === 'string' ? n.metadata.productId : null;
  return notificationPath(n) ?? (productId ? `/products/${productId}` : null);
}

/**
 * One-line action text shown after the author's name, shared by the bell and
 * the /notifications page. A watch item (#1038) names the product from
 * `metadata.productName`, because the reader was not mentioned.
 */
export function notificationVerb(n: NotificationItem): string {
  const name =
    typeof n.metadata?.productName === 'string' && n.metadata.productName
      ? n.metadata.productName
      : 'this product';
  const isWatch = n.reason === 'watch';
  // Users know a submission as an evaluation (#1039), and its assignee and
  // reviewer are told about comments without being mentioned or watching.
  const isEvaluation = n.entityType === 'submission';
  const noun = isEvaluation ? 'evaluation' : n.entityType;
  const hasProductName = typeof n.metadata?.productName === 'string' && !!n.metadata.productName;
  // Asana task events (assigned, blocked, unblocked) name the task, not "this pdlcTask".
  const task = n.entityType === 'pdlcTask' && typeof n.metadata?.taskName === 'string' ? n.metadata.taskName : null;
  if (task && n.kind === 'assignment') return hasProductName ? `assigned you ${task} on ${name}` : `assigned you ${task}`;
  if (task && n.kind === 'status_change' && n.metadata?.pdlcEvent === 'unblocked') return `— ${task} is unblocked`;
  if (task && n.kind === 'status_change' && n.metadata?.pdlcEvent === 'blocked') {
    return `— ${task} is now blocked by ${String(n.metadata.blockedBy ?? 'another task')}`;
  }
  switch (n.kind) {
    case 'mention':
    case 'comment':
      if (isWatch && isEvaluation) return 'commented on an evaluation you are on';
      return isWatch ? `commented on ${name}` : `mentioned you on this ${noun}`;
    case 'assignment':
      return `assigned you to this ${noun}`;
    case 'status_change':
      return isWatch ? `changed the status of ${name}` : `changed the status of this ${n.entityType}`;
    case 'attachment':
      return isWatch ? `updated an asset on ${name}` : `added an attachment to this ${n.entityType}`;
    case 'shipment_delivered':
      return isWatch ? `— a shipment for ${name} was delivered` : '— a shipment was delivered';
    case 'sample_received':
      return `checked in a sample of ${name}; evaluation can start`;
    case 'form_request_completed':
      return '— an NPSO fill-out request was completed';
    case 'spec_published':
      return `published the spec for ${name}`;
    case 'evaluation_submitted':
      return `submitted an evaluation of ${name}`;
    case 'evaluation_published':
      return `published an evaluation of ${name}`;
    case 'evaluation_locked':
      return hasProductName ? `locked your evaluation of ${name}` : 'locked your evaluation';
    case 'form_approval_completed':
      return `completed a form sign-off for ${name}`;
    case 'form_approval_rejected':
      return `sent a form back for ${name}`;
    case 'spec_review_requested':
      return `asked you to review the spec for ${name}`;
    case 'chase_due':
      return `reminded you to chase the factory about ${name}`;
    case 'spec_review_rejected':
      return `sent the spec for ${name} back`;
    default:
      return `updated this ${n.entityType}`;
  }
}
